import {
  SheetHeader,
  SheetPanel,
  SheetPanelClose,
  SheetPanelTitle,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import React, { useId, useLayoutEffect, useRef } from "react";

interface DataTableSheetProps {
  open: boolean;
  onClose: () => void;
  /** Heading rendered at the top of the panel; also names the landmark region. */
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Docked master/detail drawer. Uses SheetPanel for consistent visual styling
 * but rendered as a non-portalled <aside> in DOM order — so the browser's
 * native Tab order bridges grid → detail → next with no focus trap or
 * interception, while the table keeps its normal page scroll underneath.
 */
function DataTableSheet({
  open,
  onClose,
  title,
  children,
  className,
}: Readonly<DataTableSheetProps>) {
  const titleId = useId();
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);

  return (
    <SheetPanel
      open={open}
      role="region"
      aria-labelledby={titleId}
      data-slot="data-table-detail"
      className={cn("overflow-y-auto overscroll-contain", className)}
      onFocus={(e) => {
        const previous = e.relatedTarget;
        if (
          previous instanceof HTMLElement &&
          !e.currentTarget.contains(previous)
        ) {
          returnFocusRef.current = previous;
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          returnFocusRef.current?.focus();
          onClose();
        }
      }}
    >
      <SheetHeader>
        <SheetPanelTitle id={titleId} className="text-xl">
          {title}
        </SheetPanelTitle>
      </SheetHeader>
      {children}
      {/* Rendered last so Tab into the drawer reaches content first; stays
          visually top-right and is the final reachable tab stop. */}
      <SheetPanelClose
        onClick={() => {
          returnFocusRef.current?.focus();
          onClose();
        }}
      />
    </SheetPanel>
  );
}

export { DataTableSheet };
export type { DataTableSheetProps };
